import React, { useMemo } from "react";
import {
  ArrowRight,
  Check,
  Diamond,
  HelpCircle,
  MessageSquare,
} from "lucide-react";
import { parseConsumerSummary, SUMMARY_BLOCKS } from "../utils/consumerSummary";
import { convertTextToHtml } from "../utils/textProcessing";

/**
 * Order, headings and tones live in utils/consumerSummary so the PDF export
 * renders the same five blocks; only the icons are local, since the exporter
 * needs raw SVG rather than React components.
 */
const ICONS = {
  bottomLine: Diamond,
  agree: Check,
  depends: HelpCircle,
  options: ArrowRight,
  attorneyQuestions: MessageSquare,
};

/**
 * The LawFusion Summary, rendered as the coloured blocks from the consumer
 * design. The five blocks correspond exactly to the sections the summary system
 * prompt requires, so this is a presentation of real output rather than a fixed
 * layout the model has to be coerced into.
 *
 * A model that ignores the required structure still has to render: when nothing
 * parses, the whole summary falls through to a single plain block.
 */
export default function ConsumerSummary({ summary = "", formatted = "" }) {
  const parsed = useMemo(() => parseConsumerSummary(summary), [summary]);

  if (!summary.trim()) return null;

  // Once the visitor has edited the summary in the rich text editor, their own
  // markup is the summary — re-deriving the coloured blocks would throw away
  // whatever they just wrote.
  if (formatted.trim()) {
    return (
      <div
        className="lq-summary-body lq-rich"
        dangerouslySetInnerHTML={{ __html: formatted }}
      />
    );
  }

  // Unrecognised structure — show the model's own text rather than nothing.
  if (!parsed.matched) {
    return (
      <div
        className="lq-summary-body lq-rich"
        dangerouslySetInnerHTML={{ __html: convertTextToHtml(summary) }}
      />
    );
  }

  return (
    <div className="lq-summary-blocks">
      {parsed.preamble && (
        <div
          className="lq-summary-preamble lq-rich"
          dangerouslySetInnerHTML={{ __html: convertTextToHtml(parsed.preamble) }}
        />
      )}

      {SUMMARY_BLOCKS.map(({ key, tone, title }) => {
        const body = parsed.sections[key];
        if (!body) return null;
        const Icon = ICONS[key];
        return (
          <div key={key} className={`lq-block lq-block-${tone}`}>
            <div className="lq-block-icon" aria-hidden="true">
              <Icon size={19} />
            </div>
            <div className="lq-block-text">
              <h3>{title}</h3>
              <div
                className="lq-rich"
                dangerouslySetInnerHTML={{ __html: convertTextToHtml(body) }}
              />
            </div>
          </div>
        );
      })}

      {parsed.disclaimer && (
        <p className="lq-summary-disclaimer">{parsed.disclaimer}</p>
      )}
    </div>
  );
}
